<template lang="pug">
.partially-cancel-form
    .purchased-items(
        ref="container"
        @scroll="handleScroll"
        v-dragscroll
    )
        table.default-table(v-if="purchase && pagination.items.length")
            thead
                tr
                    th.min.checkbox
                        transition(name="fade")
                            i.el-icon-fa-arrow-down.fas(v-if="nothingSelected")
                    //- th.min.media
                    th.col-left.status
                    th.col-left.item {{ $t('Item') }}
                    th.col-left {{ $t('Properties') }}
                    th.dimmed.col-right {{ $t('Price') }}
                    th.col-center {{ $t('Quantity') }}
                    th.col-right {{ $t('Total') }}
            tbody
                tr.spacer
                    td(colspan="6")
                tr.item(v-for="item in pagination.items")
                    td.min.checkbox
                        el-checkbox(:value="item.selected" @change="checked => toggleItem(item, checked)")
                    //- td.min.media
                    //-     //- NOTE: neeeds article/product instance
                    //-     item-media-preview(:item="item")
                    td.col-left.status
                        template(v-if="item.status && item.status.key === 'canceled'")
                            i.el-icon-fa-exclamation-triangle.fas
                    td.col-left.item
                        .name(v-line-clamp="1")
                            b {{ item.name }}
                        small {{ item.product_nr }}
                        span &nbsp;/&nbsp;
                        small {{ item.sku }}
                    td.col-left
                        .properties
                            property-value(
                                v-for="property in item.selectedProperties"
                                :key="property.id"
                                :property="property"
                            )
                    td.min.dimmed.col-right
                        .sale-price(v-if="item.sale_price && item.sale_price < item.price")
                            .nobr
                                s.strike-through.dimmed {{ item.price | moneyFormat(purchase.currency) }}
                                //- span.percentage {{ (item.price - item.sale_price) / item.price * 100 | round(2) }}%
                            nobr.red.dimmed {{ item.sale_price | moneyFormat(purchase.currency) }}
                        nobr.dimmed(v-else) {{ item.price | moneyFormat(purchase.currency) }}
                    td.min.col-center
                        template(v-if="item.selected === true")
                            el-input-number.qty(
                                :value="item.take_back_qty"
                                size="small"
                                :min="1"
                                :max="item.qty"
                                :step-strictly="true"
                                @change="qty => updateItemTakeBackQty(item, qty)"
                            )
                        template(v-else)
                            el-input-number.qty(
                                :value="item.qty"
                                size="small"
                                disabled
                            )
                    td.min.col-right
                        template(v-if="item.selected === true")
                            .sale-price(v-if="item.sale_price && item.sale_price < item.price")
                                .nobr
                                    //- s.strike-through {{ item.price * item.take_back_qty | moneyFormat(purchase.currency) }}
                                    small.percentage {{ (item.price - item.sale_price) / item.price * 100 | round(2) }}%
                                b.nobr.red {{ item.sale_price * item.take_back_qty | moneyFormat(purchase.currency) }}
                            b.nobr(v-else) {{ item.price * item.take_back_qty | moneyFormat(purchase.currency) }}
                        template(v-else)
                            .sale-price(v-if="item.sale_price && item.sale_price < item.price")
                                .nobr
                                    //- s.strike-through {{ item.price * item.qty | moneyFormat(purchase.currency) }}
                                    small.percentage {{ (item.price - item.sale_price) / item.price * 100 | round(2) }}%
                                b.nobr.red {{ item.sale_price * item.qty | moneyFormat(purchase.currency) }}
                            b.nobr(v-else) {{ item.price * item.qty | moneyFormat(purchase.currency) }}
                tr.spacer
                    td(colspan="6")
                        .drawer-spinner-container(v-if="loading")
                            .spinner-background
                                .spinner

    .flex-row
        el-form-item.grow(
            v-if="form.items.length"
            :label="$t('Net amount to be credited')"
            prop="credited_amount_net"
        )
            el-input(
                type="number"
                v-model="creditedAmountNet"
                step="0.01"
                min="0"
            )
                template(slot="append")
                    i.el-icon-fa-euro-sign.fas(v-if="currency === '€'")
                    i.el-icon-fa-dollar-sign.fas(v-else-if="currency === '$'")
                    b.currency(v-else) {{ currency }}
        el-form-item(
            v-else
            :label="$t('Tax rate')"
        )
            el-select.is-full-width(
                placeholder="" v-model="taxRate" valueKey="id"
                :disabled="taxOptions.length <= 1"
                :class="{ 'loading': options.tax.loading }"
            )
                el-option(
                    v-for="(item, key) in taxOptions"
                    :key="item.id"
                    :label="item.label || item.name || `${item.value} %`"
                    :value="item"
                )
        el-form-item.grow(:label="$t('Gross amount to be credited')" prop="credited_amount")
            el-input(
                type="number"
                v-model="creditedAmount"
                step="0.01"
                min="0"
            )
                template(slot="append")
                    i.el-icon-fa-euro-sign.fas(v-if="currency === '€'")
                    i.el-icon-fa-dollar-sign.fas(v-else-if="currency === '$'")
                    b.currency(v-else) {{ currency }}
    el-form-item(:label="$t('Comment')" prop="comment")
        el-input(
            type="textarea"
            :autosize="{ minRows: 5, maxRows: 40 }"
            v-model="form.comment"
        )
</template>

<i18n>
{
    "de": {
        "Item": "Artikel",
        "Properties": "Attribute",
        "Price": "Preis",
        "Quantity": "Menge",
        "Total": "Gesamt",
        "Tax rate": "Steuersatz",
        "Regular": "Regulär",
        "Reduced": "Vermindert",
        "Net amount to be credited": "Netto Gutschrift-Betrag",
        "Gross amount to be credited": "Brutto Gutschrift-Betrag",
        "Could not find anything": "Es konnte nichts gefunden werden",
        "Comment": "Kommentar"
    }
}
</i18n>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex'
import { currencySymbol } from '@/filters'

import PropertyValue from '@/extensions/shop/views/Purchase/Form/Partials/PropertyValue'
import ItemMediaPreview from '@/extensions/shop/pointOfSale/views/Partials/ItemMediaPreview'

import PurchasedItemApi from '@/extensions/shop/store/api/purchasedItem';

export default {
    name: 'PartiallyCancelForm',
    props: {
        purchase: {
            type: Object,
            required: true,
        },
        form: {
            type: Object,
            required: true,
        },
        initializing: {
            type: Boolean,
            default: false,
        },
        visibility: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        loading: false,
        // credit: {
        //     credited_amount_net: 0,
        //     credited_amount: 0,
        //     comment: '',
        // },
        taxRate: undefined,
        pagination: {
            filters: {},
            purchase: undefined,
            includeStocks: false,
            includeImages: true,
            includePointOfSalePurchases: true,
            includeWebShopPurchases: true,
            includeInternals: true,
            pageInfo: {
                total: 0,
                perPage: 12,
                currentPage: 1,
                lastPage: 0,
            },
            items: []
        },
    }),
    components: {
        PropertyValue,
        ItemMediaPreview,
    },
    watch: {
        'options.tax.loading': {
            handler(value) {
                if (!value && !this.taxRate) {
                    this.taxRate = this.taxOptions.find(el => el.id === 'regular')
                        || this.taxOptions.find(el => el.id === 'reduced')
                        || this.taxOptions[0]
                }
            },
            immediate: true,
        },
        taxRate: {
            handler(taxRate) {
                const value = this.form.credited_amount || 0
                this.$set(this.form, 'credited_amount_net', _.round(value / (1 + taxRate.value / 100), 2))
            },
            immediate: true,
        },
    },
    computed: {
        creditedAmountNet: {
            get() {
                return this.form.credited_amount_net || undefined
            },
            set(value) {
                this.$set(this.form, 'credited_amount_net', parseFloat(value))

                if (this.form.items.length) {
                    let gross = 0, net = 0
                    this.pagination.items.forEach(item => {
                        if (item.selected && item.take_back_qty) {
                            const price = item.sale_price || item.price
                            const taxRate = item.tax
                            gross += item.take_back_qty * price
                            net += item.take_back_qty * price / (1 + taxRate / 100)
                        }
                    })

                    const taxRate = _.round(gross > 0 ? (gross - net) / net * 100 : 0, 6)
                    this.$set(this.form, 'credited_amount', _.round(value * (1 + taxRate / 100), 2))
                } else if (this.taxRate) {
                    this.$set(this.form, 'credited_amount', _.round(value * (1 + this.taxRate.value / 100), 2))
                }
            }
        },
        creditedAmount: {
            get() {
                return this.form.credited_amount || undefined
            },
            set(value) {
                this.$set(this.form, 'credited_amount', parseFloat(value))

                if (this.form.items.length) {
                    let gross = 0, net = 0
                    this.pagination.items.forEach(item => {
                        if (item.selected && item.take_back_qty) {
                            const price = item.sale_price || item.price
                            const taxRate = item.tax
                            gross += item.take_back_qty * price
                            net += item.take_back_qty * price / (1 + taxRate / 100)
                        }
                    })

                    const taxRate = _.round(gross > 0 ? (gross - net) / net * 100 : 0, 6)
                    this.$set(this.form, 'credited_amount_net', _.round(value / (1 + taxRate / 100), 2))
                } else if (this.taxRate) {
                    this.$set(this.form, 'credited_amount_net', _.round(value / (1 + this.taxRate.value / 100), 2))
                }
            }
        },
        taxOptions() {
            let options = []

            const regularTax = this.options.tax.items.find(el => el.default === true && el.reduced === false)
            if (regularTax) {
                options.push({
                    id: 'regular',
                    reduced: false,
                    value: regularTax.value,
                    label: this.$t('Regular')
                })
            }

            const reducedTax = this.options.tax.items.find(el => el.default === true && el.reduced === true)
            if (reducedTax) {
                options.push({
                    id: 'reduced',
                    reduced: true,
                    value: reducedTax.value,
                    label: this.$t('Reduced')
                })
            }

            const percentages = this.options.tax.items.filter(el => el.default !== true)
            return [...options, ...percentages]
        },
        nothingSelected() {
            let nothing = true
            if (this.pagination.items.length) {
                nothing = this.pagination.items.filter(el => el.selected === true).length === 0
            }
            return nothing
        },
        customer() {
            return _.get(this.purchase, 'purchaser.customer')
        },
        currency() {
            return currencySymbol(this.purchase.currency)
        },
        ...mapGetters({
            activeCms: 'activeCms',
            options: 'ext_shop_purchase/options',
        })
    },
    methods: {
        toggleItem(item, checked = false) {
            if (checked) {
                this.$set(item, 'selected', true)
                this.$set(item, 'take_back_qty', 1)
            } else {
                this.$delete(item, 'selected')
                this.$delete(item, 'take_back_qty')
            }
            this.updateCreditAmount()
            this.updateItems()
        },
        updateItemTakeBackQty(item, qty) {
            if (qty >= 1) {
                this.$set(item, 'take_back_qty', qty)
            } else {
                this.toggleItem(item)
            }
            this.updateCreditAmount()
            this.updateItems()
        },
        updateCreditAmount() {
            let amount = 0, netAmount = 0
            this.pagination.items.forEach(item => {
                if (! item.selected || ! item.take_back_qty) return
                const price = item.sale_price || item.price
                const taxRate = item.tax

                amount += item.take_back_qty * price
                netAmount += item.take_back_qty * price / (1 + taxRate / 100)
            })

            this.$set(this.form, 'credited_amount_net', _.round(netAmount, 2))
            this.$set(this.form, 'credited_amount', _.round(amount, 2))
        },
        updateItems() {
            this.form.items = []

            this.pagination.items.forEach(item => {
                if (! item.selected || ! item.take_back_qty) return
                this.form.items.push({
                    id: item.id,
                    pivot: {
                        qty: item.take_back_qty,
                    }
                })
            })
        },
        async loadPurchasedItems(page = 1) {
            this.$set(this.pagination.pageInfo, 'currentPage', page)
            this.loading = true;

            if (!this.pagination.filters) {
                this.$set(this.pagination, 'filters', {})
            }
            this.$set(this.pagination.filters, 'shop', {
                key: 'shop',
                match: this.activeCms.shop.id
            })
            // this.$set(this.pagination.filters, 'purchase_id', {
            //     key: 'purchase_id',
            //     match: this.purchase.id
            // })

            if (this.customer) {
                this.$set(this.pagination.filters, 'customer', {
                    key: 'customer',
                    match: this.customer.id
                })
            }

            const paginatedPurchasedItems = await this.fetchPurchasedItemsPaginated(this.pagination)
                .catch(err => {
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });

                    console.error(err)
                })

            if (paginatedPurchasedItems.items && paginatedPurchasedItems.items.length) {
                if (page > 1) {
                    const items = _.cloneDeep(paginatedPurchasedItems.items)
                    this.pagination.items = [...this.pagination.items, ...items]
                } else {
                    this.pagination = _.cloneDeep(paginatedPurchasedItems);
                    // this.$emit('update-visibility', true)
                    this.$emit('update:initializing', false)
                    // this.updateAutofocusSearchInput(false)
                    // this.$emit('blur-search')

                    this.$nextTick(() => {
                        if (this.$refs.container) {
                            // this.$refs.container.style['scroll-behavior'] = 'smooth';
                            this.$refs.container.scrollTop = 0
                        }
                        this.loadMoreIfNeeded(this.$refs.container)
                        // console.log(this.$refs.container)
                    })
                }
            } else {
                setTimeout(() => {
                    this.$emit('update:visibility', false)

                    this.$notify.error({
                        title: this.$t('Could not find anything'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                }, 600)
            }

            this.loading = false;
        },
        handleScroll(event) {
            const { target } = event;
            this.loadMoreIfNeeded(target)
        },
        loadMoreIfNeeded(target) {
            const currentPage = this.pagination.pageInfo.currentPage;
            if (this.loading === false && this.pagination.pageInfo.lastPage > currentPage) {

                let loadMore = false;
                const offset = 40;

                if (Math.ceil(target.scrollTop) >= target.scrollHeight - target.offsetHeight - offset) {
                    loadMore = true;
                }

                if (loadMore) {
                    this.loadPurchasedItems(currentPage + 1);
                }
            }
        },
        async fetchPurchasedItemsPaginated(payload) {
            let mimicCustomer = this.customer ? { id: this.customer.id } : null

            const customerGroup = _.get(this.customer, 'customerGroup')
                || _.get(this.activeCms, 'shop.defaultCustomerGroup')

            const address = _.get(this.purchase, 'purchaser.shippingAddress')
                || _.get(this.purchase, 'purchaser.billingAddress')

            if (!mimicCustomer && customerGroup) {
                mimicCustomer = {
                    id: null,
                    customerGroup,
                    member: { address },
                }
            }

            return PurchasedItemApi.fetchPurchasedItemsPaginated({ ...payload, mimicCustomer }, this.purchase.id);
        }
    },
    mounted() {
        this.$emit('update:initializing', true)
        this.loadPurchasedItems()

        if (!this.taxRate) {
            this.taxRate = this.taxOptions.find(el => el.id === 'regular')
                || this.taxOptions.find(el => el.id === 'reduced')
                || this.taxOptions[0]
        }
    },
}
</script>

<style lang="sass" scoped>
.drawer-spinner-container
    padding-bottom: 1rem

.partially-cancel-form
    // height: 100%
    height: calc(100vh - 66px - 76px)
    padding-left: 20px
    padding-right: 20px
    overflow-y: auto

label.el-checkbox
    &::v-deep
        .el-checkbox__input
            .el-checkbox__inner
                width: 18px
                height: 18px
                &:after
                    scale: 1.6
                    left: 6px
                    top: 2px
            &:not(.is-checked)
                .el-checkbox__inner
                    border-color: lighten($--shop-color-primary, 30%)
        &:hover,
        &:focus
            .el-checkbox__input:not(.is-checked)
                .el-checkbox__inner
                    border-color: $--shop-color-primary
.purchased-items
    // max-height: 50vh
    // max-height: calc(100vh - 66px - 76px)
    // overflow-y: auto
    // position: relative
    margin-bottom: 1rem
    thead th
        position: sticky
        top: 0

th.status,
td.status
    width: 1px
    color: $--color-danger
    font-size: 1.2rem

body table.default-table.default-table
    margin-top: 0
</style>
